import React, { useState, FormEvent } from 'react';
import { 
  Calendar, Clock, CheckCircle, ArrowRight, ArrowLeft, 
  Loader2, Building2, Ship, HardHat, Factory 
} from 'lucide-react';
import './Demo.css';

interface FormData {
  name: string;
  email: string;
  organization: string;
  industry: string;
  preferredDate: string;
  preferredTime: string;
  notes: string;
}

const Demo: React.FC = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    organization: '',
    industry: '',
    preferredDate: '',
    preferredTime: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const industries = [
    { value: 'life-sciences', label: 'Life Sciences', icon: <Building2 size={24} /> },
    { value: 'marine', label: 'Marine', icon: <Ship size={24} /> },
    { value: 'mining', label: 'Mining', icon: <HardHat size={24} /> },
    { value: 'other', label: 'Other', icon: <Factory size={24} /> },
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', 
    '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIndustrySelect = (value: string) => {
    setFormData(prev => ({ ...prev, industry: value }));
  };

  const handleTimeSelect = (time: string) => {
    setFormData(prev => ({ ...prev, preferredTime: time }));
  };

  const handleNext = () => {
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const isStep1Valid = formData.name && formData.email && formData.organization && formData.industry;
  const isStep2Valid = formData.preferredDate && formData.preferredTime;

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (isSubmitted) {
    return (
      <div className="demo-page">
        <section className="demo-hero">
          <div className="container">
            <div className="demo-success">
              <CheckCircle size={64} />
              <h1>Demo Booked!</h1>
              <p>
                Thank you for your interest in Aurexa. We've sent a confirmation email to 
                <strong> {formData.email}</strong> with your demo details and a calendar invite.
              </p>
              <div className="demo-summary">
                <div className="summary-item">
                  <Calendar size={20} />
                  <span>{new Date(formData.preferredDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <div className="summary-item">
                  <Clock size={20} />
                  <span>{formData.preferredTime}</span>
                </div>
              </div>
              <p className="prep-note">
                We'll prepare a sandbox demo aligned to your samples and compliance model. 
                Look for our prep questionnaire in your email.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="demo-page">
      {/* Hero */}
      <section className="demo-hero">
        <div className="container">
          <div className="demo-hero-content">
            <span className="badge">Book a Demo</span>
            <h1>See QC-Metric in Action</h1>
            <p>
              Book a short 30–45 minute walkthrough tailored to your industry and compliance needs. 
              We'll prepare a sandbox demo aligned to your samples and compliance model.
            </p>
          </div>
        </div>
      </section>

      {/* Wizard */}
      <section className="section">
        <div className="container">
          <div className="demo-wizard">
            {/* Progress */}
            <div className="wizard-progress">
              <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-label">Contact Details</span>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">Select Time</span>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">Confirm</span>
              </div>
            </div>

            {/* Form */}
            <div className="wizard-form">
              {/* Step 1: Contact Details */}
              {step === 1 && (
                <div className="wizard-step">
                  <h2>Contact Details</h2>
                  <p>Tell us about yourself and your organization.</p>

                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="organization">Organization *</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Your Company Name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Industry *</label>
                    <div className="industry-options">
                      {industries.map(ind => (
                        <button
                          key={ind.value}
                          type="button"
                          className={`industry-option ${formData.industry === ind.value ? 'selected' : ''}`}
                          onClick={() => handleIndustrySelect(ind.value)}
                        >
                          {ind.icon}
                          <span>{ind.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="wizard-actions">
                    <div></div>
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={handleNext}
                      disabled={!isStep1Valid}
                    >
                      Next <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Select Time */}
              {step === 2 && (
                <div className="wizard-step">
                  <h2>Select Date & Time</h2>
                  <p>Choose a convenient time for your demo.</p>

                  <div className="form-group">
                    <label htmlFor="preferredDate">Preferred Date *</label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={getMinDate()}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Available Time Slots *</label>
                    <div className="time-slots">
                      {timeSlots.map(time => (
                        <button
                          key={time}
                          type="button"
                          className={`time-slot ${formData.preferredTime === time ? 'selected' : ''}`}
                          onClick={() => handleTimeSelect(time)}
                        >
                          <Clock size={16} />
                          {time}
                        </button>
                      ))}
                    </div>
                    <p className="time-note">Times shown in your local timezone</p>
                  </div>

                  <div className="wizard-actions">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={handleBack}
                    >
                      <ArrowLeft size={18} /> Back
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={handleNext}
                      disabled={!isStep2Valid}
                    >
                      Next <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Confirm */}
              {step === 3 && (
                <form className="wizard-step" onSubmit={handleSubmit}>
                  <h2>Confirm Your Demo</h2>
                  <p>Review your details and add any additional notes.</p>

                  <div className="confirmation-card">
                    <div className="confirm-row">
                      <span className="confirm-label">Name</span>
                      <span className="confirm-value">{formData.name}</span>
                    </div>
                    <div className="confirm-row">
                      <span className="confirm-label">Email</span>
                      <span className="confirm-value">{formData.email}</span>
                    </div>
                    <div className="confirm-row">
                      <span className="confirm-label">Organization</span>
                      <span className="confirm-value">{formData.organization}</span>
                    </div>
                    <div className="confirm-row">
                      <span className="confirm-label">Industry</span>
                      <span className="confirm-value">
                        {industries.find(i => i.value === formData.industry)?.label}
                      </span>
                    </div>
                    <div className="confirm-row">
                      <span className="confirm-label">Date</span>
                      <span className="confirm-value">
                        {new Date(formData.preferredDate).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="confirm-row">
                      <span className="confirm-label">Time</span>
                      <span className="confirm-value">{formData.preferredTime}</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">Additional Notes (Optional)</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Any specific areas you'd like us to cover, current tools you're using, etc."
                    />
                  </div>

                  <div className="wizard-actions">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={handleBack}
                    >
                      <ArrowLeft size={18} /> Back
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="spin" />
                          Booking...
                        </>
                      ) : (
                        <>
                          Confirm Booking <CheckCircle size={18} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Demo;
